import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw,
  DollarSign,
  Calendar,
  AlertCircle,
  X,
  FileText,
  TrendingDown,
  ListChecks,
  Download,
  Building2,
} from 'lucide-react';
import { CompanyDeduction } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const CompanyViolationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [deductions, setDeductions] = useState<CompanyDeduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rowPdfLoading, setRowPdfLoading] = useState<string | number | null>(null);
  const [error, setError] = useState('');

  const user = apiService.getCurrentUser();
  // company id may be nested under user.company
  const companyId: string | undefined =
    (user as any)?.company?.id ||
    (user as any)?.company?._id ||
    (user as any)?.companyId ||
    undefined;

  const fetchDeductions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getCompanyDeductions(
        companyId || undefined,
      );
      setDeductions(data);
      setError('');
    } catch {
      setError(isRTL ? 'فشل تحميل الخطايا' : 'Failed to load violations');
    } finally {
      setLoading(false);
    }
  }, [companyId, isRTL]);

  useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]);

  const handleRowDownloadPdf = async (id: string | number) => {
    try {
      setRowPdfLoading(id);
      const blob = await apiService.downloadSingleDeductionPDF(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `khatiya-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(isRTL ? 'فشل تحميل الخطية' : 'Failed to download violation PDF');
    } finally {
      setRowPdfLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfLoading(true);
      const blob = await apiService.downloadDeductionsPDF(companyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'violations.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(isRTL ? 'فشل تحميل ملف PDF' : 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const totalAmount = deductions.reduce(
    (sum, d) => sum + (d.violationType?.price ?? 0),
    0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50/30 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-700 to-orange-600 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-red-900/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'الخطايا الموجهة لشركتي' : 'My Company Violations'}
                </h1>
                <p className="text-red-100 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL
                    ? 'عرض جميع الخطايا المسجلة على شركتك مع الأسباب والمبالغ'
                    : 'View all violations recorded against your company with causes and amounts'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fetchDeductions}
                  disabled={loading}
                  className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl text-white transition-all hover:scale-105 disabled:opacity-50"
                  title={isRTL ? 'تحديث' : 'Refresh'}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading || deductions.length === 0}
                  className="flex items-center gap-2 px-5 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-bold rounded-2xl transition-all hover:scale-105 border border-white/30 disabled:opacity-50"
                >
                  <Download className={`w-5 h-5 ${pdfLoading ? 'animate-bounce' : ''}`} />
                  <span className="hidden sm:inline">{isRTL ? 'تحميل PDF' : 'PDF'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 font-medium flex-1">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl border border-red-100 p-8 flex items-center gap-4">
            <div className="p-4 bg-red-600 rounded-2xl flex-shrink-0">
              <ListChecks className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                {isRTL ? 'إجمالي الخطايا' : 'Total Violations'}
              </p>
              <p className="text-3xl font-black text-gray-800">{deductions.length}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl border border-red-100 p-8 flex items-center gap-4">
            <div className="p-4 bg-orange-600 rounded-2xl flex-shrink-0">
              <TrendingDown className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                {isRTL ? 'إجمالي المبلغ المخصوم' : 'Total Amount Deducted'}
              </p>
              <p className="text-3xl font-black text-red-700">
                {totalAmount.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading && deductions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-red-600 via-red-700 to-orange-600">
                    <th className="px-6 py-5 text-right text-sm font-bold text-white uppercase tracking-wide min-w-[60px]">
                      #
                    </th>
                    <th className="px-6 py-5 text-right text-sm font-bold text-white uppercase tracking-wide min-w-[200px]" dir={isRTL ? 'rtl' : 'ltr'}>
                      {isRTL ? 'نوع الخطية' : 'Violation Type'}
                    </th>
                    <th className="px-6 py-5 text-right text-sm font-bold text-white uppercase tracking-wide min-w-[150px]">
                      {isRTL ? 'المبلغ' : 'Amount'}
                    </th>
                    <th className="px-6 py-5 text-right text-sm font-bold text-white uppercase tracking-wide min-w-[200px]" dir={isRTL ? 'rtl' : 'ltr'}>
                      {isRTL ? 'الشكوى المرتبطة' : 'Related Complaint'}
                    </th>
                    <th className="px-6 py-5 text-right text-sm font-bold text-white uppercase tracking-wide min-w-[200px]" dir={isRTL ? 'rtl' : 'ltr'}>
                      {isRTL ? 'الملاحظات / السبب' : 'Notes / Cause'}
                    </th>
                    <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wide min-w-[160px]">
                      {isRTL ? 'التاريخ' : 'Date'}
                    </th>
                    <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wide min-w-[100px]">
                      PDF
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deductions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <Building2 className="w-10 h-10 text-green-500" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">
                            {isRTL ? 'لا توجد خطايا مسجلة على شركتك' : 'No violations recorded for your company'}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {isRTL ? 'أداء ممتاز! استمر في ذلك' : 'Great performance! Keep it up'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    deductions.map((deduction, index) => (
                      <tr
                        key={deduction.id}
                        className="hover:bg-red-50/30 transition-colors"
                      >
                        <td className="px-6 py-5 text-gray-500 font-semibold text-sm">
                          {index + 1}
                        </td>
                        <td className="px-6 py-5 min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-amber-100 rounded-lg flex-shrink-0">
                              <ListChecks className="w-4 h-4 text-amber-600" />
                            </div>
                            <span className="font-bold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                              {deduction.violationType?.name || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 min-w-[150px]">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-red-100 rounded-lg flex-shrink-0">
                              <DollarSign className="w-4 h-4 text-red-600" />
                            </div>
                            <span className="font-black text-red-600 whitespace-nowrap text-lg">
                              {(deduction.violationType?.price ?? 0).toFixed(2)}
                              <span className="text-sm font-semibold ml-1">{isRTL ? 'ريال' : 'QAR'}</span>
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 min-w-[200px]">
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600 line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                              {deduction.complaint
                                ? (deduction.complaint.description?.substring(0, 70) || `#${deduction.complaint.id}`)
                                : (isRTL ? 'لا يوجد' : 'None')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 min-w-[200px]">
                          <span
                            className={`text-sm line-clamp-2 ${deduction.notes ? 'text-gray-700 font-medium' : 'text-gray-400'}`}
                            dir={isRTL ? 'rtl' : 'ltr'}
                          >
                            {deduction.notes || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-5 min-w-[160px]">
                          <div className="flex items-center justify-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <span className="text-sm whitespace-nowrap">{formatDateTime(deduction.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 min-w-[100px]">
                          <div className="flex items-center justify-center">
                            <div className="group">
                              <button
                                onClick={() => handleRowDownloadPdf(deduction.id)}
                                disabled={rowPdfLoading === deduction.id}
                                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-all font-semibold text-xs disabled:opacity-50"
                              >
                                <Download className={`w-4 h-4 flex-shrink-0 ${rowPdfLoading === deduction.id ? 'animate-bounce' : ''}`} />
                                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                  {isRTL ? 'تحميل' : 'PDF'}
                                </span>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Table footer total */}
            {deductions.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? `إجمالي ${deductions.length} خطية` : `${deductions.length} violation${deductions.length !== 1 ? 's' : ''} total`}
                </span>
                <span className="font-black text-red-700 text-lg">
                  {isRTL ? 'المجموع: ' : 'Total: '}
                  {totalAmount.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyViolationsPage;
